import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["myTasksBtn", "assigneeSelect", "projectSelect", "quadrantSelect", "banner", "bannerText", "clearBtn"]
  static values = { currentUserId: Number }

  connect() {
    this.filters = { assignee: '', project: '', quadrant: '', myTasks: false }
  }

  toggleMyTasks() {
    this.filters.myTasks = !this.filters.myTasks
    if (this.filters.myTasks) {
      this.myTasksBtnTarget.classList.add("bg-indigo-100", "dark:bg-indigo-900/40", "text-indigo-700", "dark:text-indigo-300", "border-indigo-300", "dark:border-indigo-600")
      this.myTasksBtnTarget.classList.remove("text-gray-600", "dark:text-gray-400", "border-gray-300", "dark:border-gray-600")
      // Clear assignee filter since My Tasks overrides it
      this.filters.assignee = ''
      if (this.hasAssigneeSelectTarget) {
        this.assigneeSelectTarget.value = ''
        const assigneeInput = this.assigneeSelectTarget.closest('[data-controller~="searchable-select"]')?.querySelector('[data-searchable-select-target="input"]')
        if (assigneeInput) assigneeInput.value = ''
      }
    } else {
      this.myTasksBtnTarget.classList.remove("bg-indigo-100", "dark:bg-indigo-900/40", "text-indigo-700", "dark:text-indigo-300", "border-indigo-300", "dark:border-indigo-600")
      this.myTasksBtnTarget.classList.add("text-gray-600", "dark:text-gray-400", "border-gray-300", "dark:border-gray-600")
    }
    this.apply()
  }

  filterByAssignee() {
    this.filters.assignee = this.assigneeSelectTarget.value
    // Turn off My Tasks if manually selecting assignee
    if (this.filters.assignee) {
      this.filters.myTasks = false
      this.myTasksBtnTarget.classList.remove("bg-indigo-100", "dark:bg-indigo-900/40", "text-indigo-700", "dark:text-indigo-300", "border-indigo-300", "dark:border-indigo-600")
      this.myTasksBtnTarget.classList.add("text-gray-600", "dark:text-gray-400", "border-gray-300", "dark:border-gray-600")
    }
    this.apply()
  }

  filterByProject() {
    this.filters.project = this.projectSelectTarget.value
    this.apply()
  }

  filterByQuadrant() {
    this.filters.quadrant = this.quadrantSelectTarget.value
    this.apply()
  }

  clear() {
    this.filters = { assignee: '', project: '', quadrant: '', myTasks: false }
    if (this.hasAssigneeSelectTarget) {
      this.assigneeSelectTarget.value = ''
      const assigneeInput = this.assigneeSelectTarget.closest('[data-controller~="searchable-select"]')?.querySelector('[data-searchable-select-target="input"]')
      if (assigneeInput) assigneeInput.value = ''
    }
    if (this.hasProjectSelectTarget) {
      this.projectSelectTarget.value = ''
      const projectInput = this.projectSelectTarget.closest('[data-controller~="searchable-select"]')?.querySelector('[data-searchable-select-target="input"]')
      if (projectInput) projectInput.value = ''
    }
    if (this.hasQuadrantSelectTarget) this.quadrantSelectTarget.value = ''
    this.myTasksBtnTarget.classList.remove("bg-indigo-100", "dark:bg-indigo-900/40", "text-indigo-700", "dark:text-indigo-300", "border-indigo-300", "dark:border-indigo-600")
    this.myTasksBtnTarget.classList.add("text-gray-600", "dark:text-gray-400", "border-gray-300", "dark:border-gray-600")
    this.apply()
  }

  apply() {
    const taskCards = this.element.querySelectorAll('[data-task-id]')
    const quadrants = ['do_now', 'plan', 'delegate', 'backlog', 'uncategorised']

    taskCards.forEach(card => {
      let visible = true

      if (this.filters.myTasks) {
        visible = visible && card.dataset.assignee === String(this.currentUserIdValue)
      }
      if (this.filters.assignee) {
        visible = visible && card.dataset.assignee === this.filters.assignee
      }
      if (this.filters.project) {
        visible = visible && card.dataset.project === this.filters.project
      }
      if (this.filters.quadrant) {
        visible = visible && card.dataset.quadrant === this.filters.quadrant
      }

      // Hide/show the turbo-frame wrapper so the full card is hidden
      const frame = card.closest('turbo-frame') || card
      frame.style.display = visible ? '' : 'none'
    })

    // Update empty states for each quadrant
    quadrants.forEach(q => {
      const container = document.getElementById(`quadrant_${q}`)
      if (!container) return

      const visibleTasks = container.querySelectorAll('[data-task-id]:not([style*="display: none"])')
      // Also count tasks whose turbo-frame is hidden
      const visibleFrames = Array.from(container.querySelectorAll('[data-task-id]')).filter(card => {
        const frame = card.closest('turbo-frame') || card
        return frame.style.display !== 'none'
      })

      const emptyState = document.getElementById(`empty_${q}`)
      let filterEmpty = document.getElementById(`filter_empty_${q}`)

      const hasActiveFilter = this.filters.myTasks || this.filters.assignee || this.filters.project || this.filters.quadrant

      if (visibleFrames.length === 0 && hasActiveFilter) {
        if (emptyState) emptyState.style.display = 'none'
        if (!filterEmpty) {
          // Create filter empty state
          const div = document.createElement('div')
          div.id = `filter_empty_${q}`
          div.className = 'flex flex-col items-center justify-center py-6 text-gray-400 dark:text-gray-500'
          div.innerHTML = '<i class="fas fa-filter text-lg mb-1.5"></i><p class="text-xs">No tasks match your filters</p>'
          container.appendChild(div)
        } else {
          filterEmpty.style.display = ''
        }
      } else {
        if (filterEmpty) filterEmpty.style.display = 'none'
        // Show original empty state only if there are truly no tasks at all
        if (emptyState) {
          const allTasks = container.querySelectorAll('[data-task-id]')
          emptyState.style.display = allTasks.length === 0 ? '' : 'none'
        }
      }
    })

    this._updateBanner()
  }

  _updateBanner() {
    const parts = []
    if (this.filters.myTasks) parts.push('My tasks')
    if (this.filters.assignee) {
      const wrapper = this.assigneeSelectTarget.closest('[data-controller~="searchable-select"]')
      const opt = wrapper?.querySelector(`[data-searchable-select-target="option"][data-value="${this.filters.assignee}"]`)
      parts.push(`@${opt ? opt.dataset.label || opt.textContent.trim() : this.filters.assignee}`)
    }
    if (this.filters.project) {
      parts.push(`#${this.filters.project}`)
    }
    if (this.filters.quadrant) {
      parts.push(this.filters.quadrant.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))
    }

    if (parts.length > 0) {
      this.bannerTarget.classList.remove('hidden')
      this.bannerTextTarget.textContent = `Showing: ${parts.join(' · ')}`
    } else {
      this.bannerTarget.classList.add('hidden')
    }
  }
}
